import {
  NotificationType,
  NOTIFICATION_TYPE_SUPPORTED_CHANNELS,
  type UserNotificationPreferences,
} from '@troia-v3/types';
import { useNotificationPreferences } from '@/modules/account/hooks/useNotificationPreferences';

const TARGET_TYPE = NotificationType.CONVERSATION_MESSAGE_RECEIVED;

/**
 * Deriva se o som de "Nova Mensagem" está silenciado para o usuário,
 * a partir das preferências sanitizadas vindas de `useNotificationPreferences`.
 *
 * Regras (mesmas semânticas da tela Configurações > Notificações):
 *   - sound.enabled === false                         → muted (todo som desligado)
 *   - sound.types === undefined                       → not muted (default = tudo on)
 *   - sound.types inclui CONVERSATION_MESSAGE_RECEIVED → not muted
 *   - sound.types array sem o tipo                    → muted
 */
export function deriveMuted(prefs: UserNotificationPreferences): boolean {
  const sound = prefs.sound;
  if (sound.enabled === false) return true;
  if (sound.types === undefined) return false;
  return !sound.types.includes(TARGET_TYPE);
}

/**
 * Retorna todos os NotificationTypes que suportam o canal `sound`.
 * Usado quando precisamos materializar `sound.types` (era undefined) ao silenciar
 * apenas o `CONVERSATION_MESSAGE_RECEIVED` — preserva o comportamento default
 * dos outros tipos (caso futuros tipos com som sejam adicionados ao enum).
 */
export function getAllSoundSupportedTypes(): NotificationType[] {
  return Object.entries(NOTIFICATION_TYPE_SUPPORTED_CHANNELS)
    .filter(([, channels]) => channels?.includes('sound'))
    .map(([type]) => type as NotificationType);
}

/**
 * Constrói o próximo `UserNotificationPreferences` aplicando o toggle ao tipo alvo.
 * Não toca em outros canais nem em outros tipos.
 */
export function buildToggledPreferences(
  current: UserNotificationPreferences,
  nextMuted: boolean,
): UserNotificationPreferences {
  const sound = current.sound;

  if (nextMuted) {
    if (sound.types === undefined) {
      const allSoundTypes = getAllSoundSupportedTypes();
      return {
        ...current,
        sound: {
          enabled: sound.enabled,
          types: allSoundTypes.filter((t) => t !== TARGET_TYPE),
        },
      };
    }
    return {
      ...current,
      sound: {
        enabled: sound.enabled,
        types: sound.types.filter((t) => t !== TARGET_TYPE),
      },
    };
  }

  if (sound.types === undefined) {
    return { ...current, sound: { ...sound, enabled: true } };
  }
  if (sound.types.includes(TARGET_TYPE)) {
    return { ...current, sound: { ...sound, enabled: true } };
  }
  return {
    ...current,
    sound: {
      enabled: true,
      types: [...sound.types, TARGET_TYPE],
    },
  };
}

/**
 * Hook que expõe o estado global de som de "Nova Mensagem" e a função de toggle.
 *
 * Reusa `useNotificationPreferences` (mesma fonte de verdade da tela
 * Configurações > Notificações) — herda sanitização e optimistic update.
 */
export function useMessageSoundPreference() {
  const { preferences, updatePreferences, isSaving } = useNotificationPreferences();
  const isMuted = deriveMuted(preferences);

  const toggle = () => {
    updatePreferences(buildToggledPreferences(preferences, !isMuted));
  };

  return { isMuted, toggle, isPending: isSaving };
}
